import { FC, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Text, Title, Button, List } from "@mantine/core";
import { ProductFromGetOrderByUniqueAttributeQuery } from "/@/shared/types/products/products.types";
import {
  RemovableItemOrderType,
  ImprintType,
  Rdv2ReturnMethod,
  CarrierType,
} from "../../../types/enums";
import { PrepareImprintPickup } from "../PrepareImprintPickup";

type CreateRemovableItemFullDentureInfoProps = {
  product: ProductFromGetOrderByUniqueAttributeQuery;
  dentistNotes?: string;
  orderComments?: string;
  orderReference?: string;
  practitionerName?: string;
  patientName?: string;
  clinicCode?: string;
  orderId: string;
  pickupCarrier?: CarrierType;
};

export const CreateRemovableItemFullDentureInfo: FC<
  CreateRemovableItemFullDentureInfoProps
> = ({ product, orderReference, orderId, pickupCarrier }) => {
  const newRemovableItem = product.removableItem?.[0];
  const navigate = useNavigate();

  if (!newRemovableItem) {
    return (
      <div>
        <Text>
          {"Erreur sur l'ajout de produit. Veuillez contacter le support"}
        </Text>
      </div>
    );
  }

  const orderType = newRemovableItem.fullDentureOrderType;
  const imprintType = newRemovableItem.imprintType;

  const getType = () => {
    // RDV 2 , send back with pickup
    const isRdv2Pickup =
      (imprintType === ImprintType.WAX_BITE_RDV2 ||
        imprintType === ImprintType.TRY_IN_RDV2) &&
      newRemovableItem?.returnMethod === Rdv2ReturnMethod.PICKUP;
    if (isRdv2Pickup) {
      return "rdv2_pickup";
    }

    // WAX BITE
    if (orderType === RemovableItemOrderType.WAX_BITE) {
      return "wax_bite";
    }

    // TRY_IN
    if (orderType === RemovableItemOrderType.TRY_IN) {
      return "try_in";
    }

    return null;
  };

  const type = getType();

  const handleCloseModal = () => {
    navigate(`/order/${product.orderId}`);
  };

  useEffect(() => {
    if (!type) {
      // if no content: FINAL case, we redirect directly to order
      navigate(`/order/${product.orderId}`);
    }
  }, [product.orderId]);

  if (!type) {
    return null;
  }
  // Render WAX_BITE content
  if (type === "wax_bite") {
    return (
      <Box style={{ maxWidth: 800, margin: "0 auto", padding: "1.5rem 2rem" }}>
        <Title order={3} mb="md">
          Prochaines étapes de votre commande
        </Title>

        <Box mb="sm">
          <Text fw={700} size="sm">
            Dans tous les cas
          </Text>
          <Text size="sm">
            Faites l'
            <Text component="span" fw={700}>
              essayage des cires d'occlusion en bouche du patient
            </Text>
            . Veillez à bien les adapter, marquer la ligne inter-incisale, la
            ligne des canines et ajuster l'intrados.
          </Text>
        </Box>

        <Text size="sm" mb="sm">
          Ensuite, vous disposez de deux options
        </Text>

        <Box mb="sm">
          <Text fw={700} size="sm" mb={4}>
            Option 1 - Ramassage des cires par transporteur
          </Text>
          <List type="ordered" size="sm" spacing={2}>
            <List.Item>
              Poursuivez la commande en repartant de la référence existante
              directement{" "}
              <Text component="span" fw={700} size="sm">
                sur Jasper
              </Text>{" "}
              sur la page{" "}
              <Text component="span" fw={700} size="sm">
                Détail de la commande
              </Text>
            </List.Item>
            <List.Item>
              Vous pourrez alors :
              <List withPadding size="sm" spacing={0}>
                <List.Item>
                  Demander{" "}
                  <Text component="span" fw={700} size="sm">
                    de nouvelles cires,
                  </Text>
                </List.Item>
                <List.Item>
                  Poursuivre vers{" "}
                  <Text component="span" fw={700} size="sm">
                    un essayage
                  </Text>
                </List.Item>
                <List.Item>
                  Poursuivre vers{" "}
                  <Text component="span" fw={700} size="sm">
                    une finition
                  </Text>
                </List.Item>
              </List>
            </List.Item>
          </List>
        </Box>

        <Box mb="md">
          <Text fw={700} size="sm" mb={4}>
            Option 2 - Prise d'empreinte numérique des cires en bouche du
            patient au cabinet
          </Text>
          <List type="ordered" size="sm" spacing={2}>
            <List.Item>
              Utilisez de{" "}
              <Text component="span" fw={700} size="sm">
                l'occluspray
              </Text>{" "}
              sur toute la surface des cires d'occlusions. Cela facilitera votre
              prise d'empreinte.
            </List.Item>
            <List.Item>
              Prenez une nouvelle empreinte sur 3Shape.
              <br />
              Pour chaque arcade où il y a une cire d'occlusion, il s'agit d'une{" "}
              <Text component="span" fw={700} size="sm">
                EXTRA-ORALE
              </Text>{" "}
              à 360 degrés incluant intrados + extrados{" "}
              <Text component="span" fw={700} size="sm">
                sur la même empreinte,
              </Text>{" "}
              en tenant la cire dans votre main.
            </List.Item>
            <List.Item>
              Prenez une empreinte de mordu{" "}
              <Text component="span" fw={700} size="sm">
                INTRA-ORALE
              </Text>{" "}
              avec la (ou les) cire(s) d'occlusion en bouche du patient.
            </List.Item>
            <List.Item>
              Vous pourrez alors :
              <List withPadding size="sm" spacing={0}>
                <List.Item>
                  Poursuivre vers{" "}
                  <Text component="span" fw={700} size="sm">
                    un essayage
                  </Text>
                </List.Item>
                <List.Item>
                  Poursuivre vers{" "}
                  <Text component="span" fw={700} size="sm">
                    une finition
                  </Text>
                </List.Item>
              </List>
            </List.Item>
            <List.Item>
              Prévoyez votre prochain RDV à la date de livraison prévu de
              l'essayage ou de la finition.
            </List.Item>
          </List>
        </Box>

        <Button fullWidth size="md" onClick={handleCloseModal}>
          Voir ma commande
        </Button>
      </Box>
    );
  }

  // Render TRY_IN content
  if (type === "try_in") {
    return (
      <Box style={{ maxWidth: 800, margin: "0 auto", padding: "1.5rem 2rem" }}>
        <Title order={3} mb="lg">
          Prochaines étapes de votre commande
        </Title>

        <List type="ordered" size="sm" spacing="md">
          <List.Item>
            <Text fw={700} size="sm">
              Faites l'essayage du montage dents sur cire en bouche.
            </Text>
            <Text size="sm">
              Testez aussi bien l'adaptation que l'esthétique.
            </Text>
          </List.Item>
          <List.Item>
            <Text fw={700} size="sm">
              Sur la page détail de la commande, cliquez sur la bannière pour
              poursuivre votre commande.
            </Text>
            <Text size="sm">
              Vous pourrez décider à ce moment si vous souhaitez{" "}
              <Text component="span" fw={700} size="sm">
                poursuivre vers la finition
              </Text>{" "}
              ou{" "}
              <Text component="span" fw={700} size="sm">
                demander un nouvel essayage
              </Text>
              .
            </Text>
          </List.Item>
          <List.Item>
            <Text fw={700} size="sm">
              Prévoyez votre prochain RDV patient et Jasper s'occupe du reste.
            </Text>
          </List.Item>
        </List>

        <Box mt="xl">
          <Button fullWidth size="md" onClick={handleCloseModal}>
            Voir ma commande
          </Button>
        </Box>
      </Box>
    );
  }

  // RDV2 PICKUP content
  if (type === "rdv2_pickup") {
    return (
      <PrepareImprintPickup
        orderId={orderId}
        orderReference={orderReference}
        imprintType={imprintType}
        pickupCarrier={pickupCarrier}
      />
    );
  }

  return null;
};
